import React, { useState, useEffect, useMemo } from 'react';
import Plot from 'react-plotly.js';
import { ControlPanel, ControlGroup, DataTable, Loader } from '../components/Components';
import './gene.css';

// Searchable Select Component
const SearchableSelect = React.memo(({ options = [], value, onChange, placeholder, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        return options.filter(option =>
            option.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, options]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.searchable-select')) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleSelect = (option) => {
        onChange(option);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="searchable-select">
<div className="searchable-select-input" onClick={() => setIsOpen(true)}>
                <input
                    type="text"
                    value={isOpen ? searchTerm : value || ''}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={placeholder}
                    onFocus={() => setIsOpen(true)}
                    className="search-input"
                />
                <svg className="dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>

            {isOpen && (
                <div className="searchable-select-dropdown">
                    <div className="dropdown-content">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={index}
                                    className={`dropdown-option ${option === value ? 'selected' : ''}`}
                                    onClick={() => handleSelect(option)}
                                >
                                    {option}
                                </div>
                            ))
                        ) : (
                            <div className="dropdown-no-results">No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

// Clean DataTable component
const CleanDataTable = ({ data, title, isDynamic = false }) => {

    if (!data || data.length === 0) {
        return (
            <div className="gene-glass-card" style={{ height: '100%' }}>
                <div className="gene-card-title">
                    {title}
                    <span className="gene-table-count">0 items</span>
                </div>
                <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                    No data available
                </div>
            </div>
        );
    }
    
    const headers = Object.keys(data[0]);

    // Reorder headers to put reaction columns first
    let orderedHeaders = [...headers];
    const reactionNameCol = headers.find(h => h.includes('reaction') && h.includes('name'));
    const reactionIdCol = headers.find(h => h.includes('reaction') && !h.includes('name') && !h.includes('direction'));

    if (reactionNameCol) {
        orderedHeaders = [reactionNameCol, ...orderedHeaders.filter(h => h !== reactionNameCol)];
    } else if (reactionIdCol) {
        orderedHeaders = [reactionIdCol, ...orderedHeaders.filter(h => h !== reactionIdCol)];
    }

    // Function to format column names
    const formatColumnName = (header) => {
        return header
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    return (
        <div className="gene-glass-card" style={{ height: '100%' }}>
            <div className="gene-card-title">
                {title}
                <span className="gene-table-count">{data.length} items</span>
            </div>
            <div className={`gene-table-wrapper ${isDynamic ? 'gene-table-dynamic' : ''}`} style={{ maxHeight: 'calc(100%)', overflow: 'auto', position: 'relative' }}>
                <table className="gene-enhanced-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                    <colgroup>
                        {orderedHeaders.map(header => (
                            <col
                                key={header}
                                style={{
                                    width: header.includes('reaction_no_direction') ? '8%' :
                                           header.includes('reaction') && header.includes('name') ? '25%' :
                                           'auto'
                                }}
                            />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            {orderedHeaders.map(header => (
                                <th key={header}>
                                    {formatColumnName(header)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr style={{ height: '2px' }}><td colSpan={orderedHeaders.length} style={{ padding: 0, border: 'none' }}></td></tr>
                        {data.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {orderedHeaders.map(header => (
                                    <td
                                        key={header}
                                        className={isDynamic ? '' : 'gene-table-cell'}
                                        style={{
                                            fontWeight: (header.includes('reaction') && header.includes('name')) ||
                                                       (header.includes('reaction') && !header.includes('name') && !header.includes('direction')) ? 'bold' : 'normal',
                                            color: (header.includes('reaction') && header.includes('name')) ||
                                                  (header.includes('reaction') && !header.includes('name') && !header.includes('direction')) ? '#6b7280' : 'inherit',
                                            width: header.includes('reaction_no_direction') ? '8%' :
                                                   header.includes('reaction') && header.includes('name') ? '25%' :
                                                   'auto'
                                        }}
                                        title={String(row[header] || '')}
                                    >
                                        {typeof row[header] === 'number' ? (
                                            Number(row[header]).toFixed(4) 
                                        ) : (
                                            String(row[header] || '')
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>


            </div>
        </div>
    );
};

function AgingPage({ 
    selections, 
    handleSelection, 
    options, 
    data, 
    isTabLoading,
    calculateTrendline 
}) {
    const agingCellTypes = selections.agingCellRef === 'HPA' ? options.immuneCellTypesHPA : options.immuneCellTypesMonaco;
    const shouldShowLoading = isTabLoading;

    // Clean plot configuration
    const plotConfig = {
        displayModeBar: false,
        responsive: true
    };

    const plotLayout = {
        font: { family: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', size: 11 },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(248, 250, 252, 0.5)',
        margin: { l: 50, r: 20, t: 20, b: 50 },
        showlegend: false
    };

    // Mode change handler
    const handleModeChange = (mode) => {
        handleSelection('agingAnalysisMode', mode);
    };

    const currentMode = selections.agingAnalysisMode || 'genes';

    // Generate scatter plot with trendline
    const createScatterPlot = (dataPoints, xField, yField, title, xTitle, yTitle) => {
        if (!dataPoints || dataPoints.length === 0) return [];

        const trendline = calculateTrendline ? calculateTrendline(
            dataPoints.map(d => ({AGE: d[xField], expression: d[yField]}))
        ) : null;

        const plotData = [
            {
                x: dataPoints.map(d => parseFloat(d[xField])),
                y: dataPoints.map(d => parseFloat(d[yField])),
                mode: 'markers',
                type: 'scatter',
                marker: { 
                    color: 'rgba(59, 130, 246, 0.6)', 
                    size: 6,
                    opacity: 0.7,
                    line: { color: 'white', width: 1 }
                },
                hovertemplate: `<b>${xTitle}: %{x}</b><br>${yTitle}: %{y:.3f}<extra></extra>`,
                name: ''
            }
        ];

        if (trendline) {
            plotData.push({
                x: trendline.x,
                y: trendline.y,
                mode: 'lines',
                type: 'scatter',
                line: { color: '#1e40af', width: 3 },
                hovertemplate: 'Trend Line<extra></extra>',
                name: ''
            });
        }

        return plotData;
    };

    return (
        <div className="gene-page">
            {/* Sidebar */}
            <div className="gene-sidebar">
                <h1 className="gene-sidebar-title">Aging Signatures</h1>
                
                <div className="gene-sidebar-section">
                    <p className="gene-sidebar-label">Analysis Mode</p>
                    <div className="gene-mode-controls">
                        <button
                            className={`gene-mode-btn ${currentMode === 'genes' ? 'active' : ''}`}
                            onClick={() => handleModeChange('genes')}
                        >
                            Aging Associated Genes
                        </button>
                        <button
                            className={`gene-mode-btn ${currentMode === 'clusters' ? 'active' : ''}`}
                            onClick={() => handleModeChange('clusters')}
                        >
                            Aging Associated Gene Clusters
                        </button>
                        <button
                            className={`gene-mode-btn ${currentMode === 'cells' ? 'active' : ''}`}
                            onClick={() => handleModeChange('cells')}
                        >
                            Cell Features
                        </button>
                        <button
                            className={`gene-mode-btn ${currentMode === 'metabolism' ? 'active' : ''}`}
                            onClick={() => handleModeChange('metabolism')}
                        >
                            Metabolic Features
                        </button>
                    </div>
                </div>

                {/* Mode-specific controls */}
                {currentMode === 'genes' && (
                    <>
                        <div className="gene-sidebar-section">
                            <p className="gene-sidebar-label">Reported Aging Associated Gene</p>
                            <SearchableSelect
                                options={options.reportedAgingGenes || []}
                                value={selections.agingReportedGene}
                                onChange={(value) => handleSelection('agingReportedGene', value)}
                                placeholder="Search reported genes..."
                            />
                        </div>
                        <div className="gene-sidebar-section">
                            <p className="gene-sidebar-label">Identified Aging Associated Gene</p>
                            <SearchableSelect
                                options={options.agingGenes || []}
                                value={selections.agingCorrelatedGene}
                                onChange={(value) => handleSelection('agingCorrelatedGene', value)}
                                placeholder="Search aging genes..."
                            />
                        </div>
                    </>
                )}

                {currentMode === 'clusters' && (
                    <div className="gene-sidebar-section">
                        <p className="gene-sidebar-label">Select Module</p>
                        <SearchableSelect
                            options={options.agingModules || []}
                            value={selections.agingModule}
                            onChange={(value) => handleSelection('agingModule', value)}
                            placeholder="Search modules..."
                        />
                    </div>
                )}

                {currentMode === 'cells' && (
                    <>
                        <div className="gene-sidebar-section">
                            <p className="gene-sidebar-label">Reference</p>
                            <div className="gene-mode-controls">
                                <button
                                    className={`gene-mode-btn ${selections.agingCellRef === 'HPA' ? 'active' : ''}`}
                                    onClick={() => handleSelection('agingCellRef', 'HPA')}
                                >
                                    HPA
                                </button>
                                <button
                                    className={`gene-mode-btn ${selections.agingCellRef === 'Monaco' ? 'active' : ''}`}
                                    onClick={() => handleSelection('agingCellRef', 'Monaco')}
                                >
                                    Monaco
                                </button>
                            </div>
                        </div>
                        <div className="gene-sidebar-section">
                            <p className="gene-sidebar-label">Cell Type</p>
                            <SearchableSelect
                                options={agingCellTypes || []}
                                value={selections.agingCellType}
                                onChange={(value) => handleSelection('agingCellType', value)}
                                placeholder="Search cell types..."
                            />
                        </div>
                    </>
                )}

                {currentMode === 'metabolism' && (
                    <>
                        <div className="gene-sidebar-section">
                            <p className="gene-sidebar-label">Subsystem</p>
                            <SearchableSelect
                                options={options.subsystems || []}
                                value={selections.agingSubsystem}
                                onChange={(value) => handleSelection('agingSubsystem', value)}
                                placeholder="Search subsystems..."
                            />
                        </div>
                        <div className="gene-sidebar-section">
                            <p className="gene-sidebar-label">Reaction</p>
                            <SearchableSelect
                                options={options.agingReactions || []}
                                value={selections.agingReaction}
                                onChange={(value) => handleSelection('agingReaction', value)}
                                placeholder="Search reactions..."
                            />
                        </div>
                        <div className="gene-sidebar-section">
                            <p className="gene-sidebar-label">Correlation Filter</p>
                            <div className="gene-mode-controls">
                                <button
                                    className={`gene-mode-btn ${selections.agingCorDir === 'Positive' ? 'active' : ''}`}
                                    onClick={() => handleSelection('agingCorDir', 'Positive')}
                                >
                                    Positive
                                </button>
                                <button
                                    className={`gene-mode-btn ${selections.agingCorDir === 'Negative' ? 'active' : ''}`}
                                    onClick={() => handleSelection('agingCorDir', 'Negative')}
                                >
                                    Negative
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Main Content */}
            <div className="gene-main-content">
                {/* Loading State */}
                {shouldShowLoading && (
                    <div className="gene-loading">
                        <div className="gene-loading-spinner"></div>
                        Loading aging analysis data...
                    </div>
                )}

                {/* Content for genes mode */}
                {!shouldShowLoading && currentMode === 'genes' && (
                    <div className="gene-content">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                            {/* Reported Aging Genes Plot + Table Side by Side */}
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                                {/* Plot - 50% width */}
                                <div style={{ flex: '1' }}>
                                    <div className="gene-glass-card">
                                        <div className="gene-card-title">
                                            Previous Reported Aging Genes
                                            <span className="gene-tag">{selections.agingReportedGene}</span>
                                        </div>
                                        <p className="gene-card-subtitle">
                                            Correlation between reported aging gene expression and age
                                        </p>

                                        <div className="gene-plot-container" style={{ height: '400px', overflow: 'hidden' }}>
                                            <Plot
                                                data={createScatterPlot(
                                                    data.agingReportedData,
                                                    'AGE',
                                                    'expression',
                                                    'Reported Aging Gene',
                                                    'Age',
                                                    'Expression'
                                                )}
                                                layout={{
                                                    ...plotLayout,
                                                    height: 400,
                                                    xaxis: {
                                                        title: { text: 'Age', font: { size: 14, color: '#374151' } },
                                                        tickfont: { size: 12, color: '#6b7280' },
                                                        gridcolor: 'rgba(0,0,0,0.1)',
                                                        showgrid: true
                                                    },
                                                    yaxis: {
                                                        title: { text: 'Expression', font: { size: 14, color: '#374151' } },
                                                        tickfont: { size: 12, color: '#6b7280' },
                                                        gridcolor: 'rgba(0,0,0,0.1)',
                                                        showgrid: true
                                                    }
                                                }}
                                                config={plotConfig}
                                                useResizeHandler={true}
                                                style={{ width: '100%', height: '100%' }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Table - 50% width */}
                                <div style={{ flex: '1' }}>
                                    <CleanDataTable
                                        data={data.agingReportedData?.filter((v,i,a)=>a.findIndex(t=>(t.rho === v.rho && t.padj === v.padj))===i).map(({rho, padj})=>({rho, padj}))}
                                        title="Reported Gene Correlation"
                                    />
                                </div>
                            </div>

                            {/* Identified Aging Genes Plot + Table Side by Side */}
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                                {/* Plot - 50% width */}
                                <div style={{ flex: '1' }}>
                                    <div className="gene-glass-card">
                                        <div className="gene-card-title">
                                            Identified Aging Genes
                                            <span className="gene-tag">{selections.agingCorrelatedGene}</span>
                                        </div>
                                        <p className="gene-card-subtitle">
                                            Gene expression correlation with age from our data
                                        </p>

                                        <div className="gene-plot-container" style={{ height: '400px', overflow: 'hidden' }}>
                                            <Plot
                                                data={createScatterPlot(
                                                    data.agingCorrelatedData,
                                                    'AGE',
                                                    'expression',
                                                    'Identified Aging Gene',
                                                    'Age',
                                                    'Expression'
                                                )}
                                                layout={{
                                                    ...plotLayout,
                                                    height: 400,
                                                    xaxis: {
                                                        title: { text: 'Age', font: { size: 14, color: '#374151' } },
                                                        tickfont: { size: 12, color: '#6b7280' },
                                                        gridcolor: 'rgba(0,0,0,0.1)',
                                                        showgrid: true
                                                    },
                                                    yaxis: {
                                                        title: { text: 'Expression', font: { size: 14, color: '#374151' } },
                                                        tickfont: { size: 12, color: '#6b7280' },
                                                        gridcolor: 'rgba(0,0,0,0.1)',
                                                        showgrid: true
                                                    }
                                                }}
                                                config={plotConfig}
                                                useResizeHandler={true}
                                                style={{ width: '100%', height: '100%' }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Table - 50% width */}
                                <div style={{ flex: '1' }}>
                                    <CleanDataTable
                                        data={data.agingCorrelatedData?.filter((v,i,a)=>a.findIndex(t=>(t.rho === v.rho && t.padj === v.padj))===i).map(({rho, padj})=>({rho, padj}))}
                                        title="Identified Gene Correlation Statistics"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Content for clusters mode */}
                {!shouldShowLoading && currentMode === 'clusters' && (
                    <div className="gene-content">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            
                            {/* Module Eigengenes Plot + Module Correlation Table Side by Side */}
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                                {/* Plot - 50% width */}
                                <div style={{ flex: '1' }}>
                                    <div className="gene-glass-card">
                                        <div className="gene-card-title">
                                            Co-expression Clusters Related to Aging
                                            <span className="gene-tag">{selections.agingModule}</span>
                                        </div>
                                        <p className="gene-card-subtitle">
                                            Module eigengene expression patterns across age groups
                                        </p>

                                        <div className="gene-plot-container" style={{ height: '400px', overflow: 'hidden' }}>
                                            <Plot
                                                data={createScatterPlot(
                                                    data.agingModuleEigengenes?.map(d => ({AGE: d.AGE, expression: d.value})),
                                                    'AGE',
                                                    'expression',
                                                    'Module Eigengenes',
                                                    'Age',
                                                    selections.agingModule
                                                )}
                                                layout={{
                                                    ...plotLayout,
                                                    height: 400,
                                                    xaxis: {
                                                        title: { text: 'Age', font: { size: 14, color: '#374151' } },
                                                        tickfont: { size: 12, color: '#6b7280' },
                                                        gridcolor: 'rgba(0,0,0,0.1)',
                                                        showgrid: true
                                                    },
                                                    yaxis: {
                                                        title: { text: selections.agingModule, font: { size: 14, color: '#374151' } },
                                                        tickfont: { size: 12, color: '#6b7280' },
                                                        gridcolor: 'rgba(0,0,0,0.1)',
                                                        showgrid: true
                                                    }
                                                }}
                                                config={plotConfig}
                                                useResizeHandler={true}
                                                style={{ width: '100%', height: '100%' }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Module Correlation Table - 50% width */}
                                <div style={{ flex: '1' }}>
                                    <CleanDataTable
                                        data={data.agingModuleCorr}
                                        title="Module Correlation with Age"
                                    />
                                </div>
                            </div>

                            {/* Remaining Tables Side by Side */}
                            <div style={{ display: 'flex', gap: '20px' }}>
                                <div style={{ flex: '1' }}>
                                    <CleanDataTable
                                        data={data.agingModuleGenes?.map(gene => ({gene}))}
                                        title="Genes in Module"
                                    />
                                </div>
                                <div style={{ flex: '1' }}>
                                    {/* Placeholder for balance */}
                                    <div></div>
                                </div>
                            </div>

                            {/* GSOA Enrichment Table */}
                            <CleanDataTable 
                                data={data.agingModuleGsoa} 
                                title="GSOA Enrichment Analysis" 
                            />
                        </div>
                    </div>
                )}

                {/* Content for cells mode */}
                {!shouldShowLoading && currentMode === 'cells' && (
                    <div className="gene-content">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                            {/* Cell Type Plot + Table Side by Side */}
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                                {/* Plot - 50% width */}
                                <div style={{ flex: '1' }}>
                                    <div className="gene-glass-card">
                                        <div className="gene-card-title">
                                            Cell Type Related to Aging
                                            <span className="gene-tag">{selections.agingCellType}</span>
                                        </div>
                                        <p className="gene-card-subtitle">
                                            Cell type abundance patterns across age groups ({selections.agingCellRef} reference)
                                        </p>

                                        <div className="gene-plot-container" style={{ height: '400px', overflow: 'hidden' }}>
                                            <Plot
                                                data={createScatterPlot(
                                                    data.agingCellData?.map(d => ({AGE: d.AGE, expression: d.value})),
                                                    'AGE',
                                                    'expression',
                                                    'Cell Type Analysis',
                                                    'Age',
                                                    selections.agingCellType
                                                )}
                                                layout={{
                                                    ...plotLayout,
                                                    height: 400,
                                                    xaxis: {
                                                        title: { text: 'Age', font: { size: 14, color: '#374151' } },
                                                        tickfont: { size: 12, color: '#6b7280' },
                                                        gridcolor: 'rgba(0,0,0,0.1)',
                                                        showgrid: true
                                                    },
                                                    yaxis: {
                                                        title: { text: selections.agingCellType, font: { size: 14, color: '#374151' } },
                                                        tickfont: { size: 12, color: '#6b7280' },
                                                        gridcolor: 'rgba(0,0,0,0.1)',
                                                        showgrid: true
                                                    }
                                                }}
                                                config={plotConfig}
                                                useResizeHandler={true}
                                                style={{ width: '100%', height: '100%' }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Table - 50% width */}
                                <div style={{ flex: '1' }}>
                                    <CleanDataTable
                                        data={data.agingCellCorr}
                                        title="Cell Type Correlation with Age"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Content for metabolism mode */}
                {!shouldShowLoading && currentMode === 'metabolism' && (
                    <div className="gene-content">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                            {/* Metabolic Features Plot + Reaction Correlation Table Side by Side */}
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                                {/* Plot - 50% width */}
                                <div style={{ flex: '1' }}>
                                    <div className="gene-glass-card">
                                        <div className="gene-card-title">
                                            Metabolic Features in Subsystem
                                            <span className="gene-tag">{selections.agingReaction}</span>
                                        </div>
                                        <p className="gene-card-subtitle">
                                            Reaction activity patterns across age groups ({selections.agingSubsystem})
                                        </p>

                                        <div className="gene-plot-container" style={{ height: '400px', overflow: 'hidden' }}>
                                            <Plot
                                                data={createScatterPlot(
                                                    data.agingReactionData?.map(d => ({AGE: d.AGE, expression: d.activity})),
                                                    'AGE',
                                                    'expression',
                                                    'Metabolic Features',
                                                    'Age',
                                                    'Reaction Activity'
                                                )}
                                                layout={{
                                                    ...plotLayout,
                                                    height: 400,
                                                    xaxis: {
                                                        title: { text: 'Age', font: { size: 14, color: '#374151' } },
                                                        tickfont: { size: 12, color: '#6b7280' },
                                                        gridcolor: 'rgba(0,0,0,0.1)',
                                                        showgrid: true
                                                    },
                                                    yaxis: {
                                                        title: { text: 'Reaction Activity', font: { size: 14, color: '#374151' } },
                                                        tickfont: { size: 12, color: '#6b7280' },
                                                        gridcolor: 'rgba(0,0,0,0.1)',
                                                        showgrid: true
                                                    }
                                                }}
                                                config={plotConfig}
                                                useResizeHandler={true}
                                                style={{ width: '100%', height: '100%' }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Reaction Correlation Table - 50% width */}
                                <div style={{ flex: '1' }}>
                                    <CleanDataTable
                                        data={data.agingReactionCorr}
                                        title="Reaction Correlation with Age"
                                    />
                                </div>
                            </div>

                            {/* Second Table Full Width */}
                            <CleanDataTable
                                data={data.agingMetabolicFiltered}
                                title={`${selections.agingCorDir} Correlation Features`}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AgingPage;
